import { useMutation } from "@apollo/client";
import { showSnackbar } from "@/lib/snackbarUtils";
import { CREATE_JOB_POST_MUTATION } from "@/components/Forms/JobPostForm/queries";
import usePageTitle from "@/hooks/usePageTitle";
import { useNavigate } from "react-router-dom";
import JobPostForm from "@/components/Forms/JobPostForm";
import { JobPostFormInput } from "@/components/Forms/JobPostForm/types";
import { useRef, useState, useEffect, useMemo } from "react";
import useConvertedJobData from "@/hooks/useConvertedJobData";
import { fullyDecodeHtmlEntities } from "@/lib/htmlHelpers";
import { ApplicationQuestion, Option, PostStage } from "@/pages/_layouts/jobs/types";
import {
  formatSubmissionDeadlineForMutation,
  isAdvancedOrExecutiveWithQuestions,
  isSubmissionDeadlineInPast
} from "@/components/Forms/JobPostForm/utils";

export default function CreateJobPostPage() {
  usePageTitle("/jobs/job-post/create", "Jobs | Create Job Post");
  const formRef = useRef<{ getValues: () => JobPostFormInput } | null>(null);
  const navigate = useNavigate();
  const { convertedJobData } = useConvertedJobData();
  const dataInitializedRef = useRef(false);

  const [createJobPost] = useMutation(CREATE_JOB_POST_MUTATION);
  const segments = window.location.pathname.split("/");
  const basePath = segments.slice(0, -2).join("/");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSecondarySubmitting, setIsSecondarySubmitting] = useState(false);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const [errorFacilityId, setErrorFacilityId] = useState<string | null>(null);
  const confettiTriggeredRef = useRef(false);
  const [triggerConfettiInForm, setTriggerConfettiInForm] = useState(false);
  const [shouldNavigateAfterConfetti, setShouldNavigateAfterConfetti] = useState(false);
  const [createdJobPostId, setCreatedJobPostId] = useState<string | null>(null);

  const defaultValues = useMemo(() => {
    if (!convertedJobData) {
      return null;
    }
    return JSON.parse(JSON.stringify(convertedJobData));
  }, [convertedJobData]);

  const hasJobImportData = defaultValues && Object.keys(defaultValues).length > 0;

  useEffect(() => {
    if (hasJobImportData && !dataInitializedRef.current) {
      dataInitializedRef.current = true;
      if (!confettiTriggeredRef.current) {
        setTriggerConfettiInForm(true);
        confettiTriggeredRef.current = true;
      }
      // Clean up stored data so future navigations start fresh.
      localStorage.removeItem("convertedJobData");

      return () => {};
    }
  }, [defaultValues, hasJobImportData]);

  async function handleSubmit(data: any, isSecondaryAction?: boolean) {
    try {
      if (isSecondaryAction) {
        setIsSecondarySubmitting(true);
      } else {
        setIsSubmitting(true);
      }
      const {
        facility,
        facilityName,
        sectionId,
        locationType,
        consultant,
        employerData,
        isDraft = false,
        isValid,
        overview,
        submissionDeadline,
        compensation,
        pointOfContact,
        requiredExperienceInYears,
        requiredEducation,
        additionalPgaEducation,
        application,
        ...inputData
      } = data;
      const decodedOverview = overview ? fullyDecodeHtmlEntities(overview) : "";
      let stage = data.sendForReview ? PostStage.IN_REVIEW : isValid && !isDraft ? PostStage.ACTIVE : PostStage.DRAFT;

      const payload = {
        ...inputData,
        overview: decodedOverview,
        stage: stage,
        requiredExperienceInYears: parseInt(requiredExperienceInYears),
        requiredEducation: requiredEducation === "N/A" ? null : requiredEducation,
        additionalPgaEducation,
        compensation: {
          baseMaximum: compensation.baseMaximum !== undefined ? compensation.baseMaximum / 100 : null,
          baseMinimum: compensation.baseMinimum !== undefined ? compensation.baseMinimum / 100 : null,
          totalAnticipated: compensation.totalAnticipated !== undefined ? compensation.totalAnticipated / 100 : null,
          currencyUnit: "USD"
        },
        pointOfContact: {
          name: pointOfContact?.name || "",
          email: pointOfContact?.email || "",
          phone: pointOfContact?.phone || "",
          title: pointOfContact?.title || ""
        }
      };
      payload.employerId = payload.employerId || null;
      if (submissionDeadline && submissionDeadline.startDate) {
        payload.submissionDeadline = formatSubmissionDeadlineForMutation(submissionDeadline.startDate);

        const deadlineInPast = isSubmissionDeadlineInPast(submissionDeadline.startDate);
        if (deadlineInPast) {
          payload.stage = PostStage.UNPOSTED;
        }
      } else {
        payload.submissionDeadline = null;
      }

      const applicationPayload = {
        questions: data.application?.questions.map((question: ApplicationQuestion, index: number) => ({
          content: question.content,
          position: index + 1,
          questionType: question.questionType,
          options: question.options.map((option: Option, optionIndex: number) => ({
            content: option.content,
            position: optionIndex + 1
          }))
        }))
      };

      const response = await createJobPost({
        variables: {
          input: payload,
          ...(isAdvancedOrExecutiveWithQuestions(data) ? { application: applicationPayload } : {})
        }
      });

      if (response.data.createJobPost.success) {
        // Only trigger confetti if we're publishing (not drafting)
        if (!isDraft) {
          setTriggerConfettiInForm(true);
          setShouldNavigateAfterConfetti(true);
          setCreatedJobPostId(response.data.createJobPost.jobPost.id);
        } else {
          // For drafts, navigate immediately
          navigate(`${basePath}/job-post/${response.data.createJobPost.jobPost.id}/details`);
        }
        const successText = inputData.sendForReview
          ? "Job post successfully created and sent for review!"
          : "Job post created successfully!";
        showSnackbar(isDraft ? "Job post drafted successfully!" : successText);
      } else {
        const errorMessage = response.data.createJobPost.message;

        if (errorMessage === "Employer does not have permissions at assigned facility." && data.facilityId) {
          setErrorBanner("Employer does not have Manage Job role at assigned facility.");
          setErrorFacilityId(data.facilityId);
        } else {
          setErrorBanner(errorMessage);
          setErrorFacilityId(null);
        }
      }
    } catch (e) {
      setErrorBanner("Error creating job post");
    } finally {
      setIsSubmitting(false);
      setIsSecondarySubmitting(false);
    }
  }

  return (
    <>
      <JobPostForm
        onSubmit={handleSubmit}
        formRef={formRef}
        isSubmitting={isSubmitting}
        defaultValues={defaultValues}
        isSecondarySubmitting={isSecondarySubmitting}
        triggerConfetti={triggerConfettiInForm}
        errorBanner={errorBanner || undefined}
        setErrorBanner={(errorBanner) => setErrorBanner(errorBanner || null)}
        errorFacilityId={errorFacilityId}
        setErrorFacilityId={(facilityId) => setErrorFacilityId(facilityId || null)}
        onConfettiComplete={() => {
          setTriggerConfettiInForm(false);
          if (shouldNavigateAfterConfetti && createdJobPostId) {
            setShouldNavigateAfterConfetti(false);
            navigate(`${basePath}/job-post/${createdJobPostId}/details`);
          }
        }}
      />
    </>
  );
}
